import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Plus, X, FileLock, PackageSearch } from "lucide-react";
import useAlert from "../components/hook/useAlert";
import callApi from "../helpers/networkHelper";
import API_CONFIG from "../config/apiConstant";
import createProductHandlerRegistry from "../modules/licenseProducts/productHandlerRegistry";
import Pagination from "../components/common/Pagination";
import LicenseFilterSection from "../components/common/LicenseFilterSection";
import PageSkeleton from "../components/common/PageSkelton";
import PageHeader from "../components/common/PageHeader";
import DataTable from "../components/common/DataTable";
import EmptyState from "../components/common/EmptyState";
import ConfirmDialog from "../components/common/ConfirmDialog";

const PAGE_SIZE = 10;

const getReadableErrorMessage = (error) => {
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
};

const FinalLicenseGeneration = () => {
    const { showSuccess, showError } = useAlert();
    const showErrorRef = useRef(showError);

    const [products, setProducts] = useState([]);
    const [selectedProductKey, setSelectedProductKey] = useState(null);
    const [formProductKey, setFormProductKey] = useState(null);
    const [productFormData, setProductFormData] = useState({});
    const [defaultProductKey, setDefaultProductKey] = useState(
        localStorage.getItem("defaultLicenseProduct") || ""
    );
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPreparingForm, setIsPreparingForm] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);

    // "create" issues a new license; "edit" re-signs the row in editingLicense
    // with new entitlements. One modal serves both — the form itself locks the
    // fields an edit may not touch.
    const [formMode, setFormMode] = useState("create");
    const [editingLicense, setEditingLicense] = useState(null);

    const [tableFilters, setTableFilters] = useState({});
    const [tableRows, setTableRows] = useState([]);
    const [tableTotal, setTableTotal] = useState(0);
    const [tablePage, setTablePage] = useState(1);

    const [filterMetadata, setFilterMetadata] = useState({});

    const [deletingLicense, setDeletingLicense] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        showErrorRef.current = showError;
    }, [showError]);

    const productHandlers = useMemo(
        () =>
            createProductHandlerRegistry({
                callApi,
                API_CONFIG,
                showError,
                showSuccess,
                getReadableErrorMessage,
            }),
        [] // showError / showSuccess are recreated by useAlert, but their behavior is stable
    );

    const selectedProduct = products.find((product) => product.key === selectedProductKey) || null;
    const selectedHandler = selectedProductKey ? productHandlers[selectedProductKey] : null;

    const formProduct =
        products.find((product) => product.key === formProductKey) || null;

    const formHandler =
        formProductKey ? productHandlers[formProductKey] : null;

    const fetchEnabledProducts = async () => {
        try {
            const res = await callApi(
                API_CONFIG.LICENSE_PRODUCTS.ENABLED.METHOD,
                API_CONFIG.LICENSE_PRODUCTS.ENABLED.ENDPOINT
            );

            const enabled = res.data.data || [];

            setProducts(enabled);

            const savedDefault =
                localStorage.getItem("defaultLicenseProduct");

            if (
                savedDefault &&
                enabled.some((p) => p.key === savedDefault)
            ) {
                setSelectedProductKey(savedDefault);
            } else {
                setSelectedProductKey(null);
            }
        } catch (error) {
            showError(getReadableErrorMessage(error));
        }
    };

    useEffect(() => {
        const init = async () => {
            setIsPageLoading(true);
            // await new Promise(resolve =>
            //     setTimeout(resolve, 1000)
            // );
            await fetchEnabledProducts();
            setIsPageLoading(false);
        };

        init();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const loadFilters = async () => {
            if (!selectedHandler?.loadFilterData) {
                setFilterMetadata({});
                return;
            }

            const result = await selectedHandler.loadFilterData();

            if (result?.ok) {
                setFilterMetadata(result.data);
            }
        };

        loadFilters();
    }, [selectedProductKey]);

    const resetFilters = useCallback(() => {
        if (!selectedHandler) return;
        setTableFilters(selectedHandler.defaultFilters || {});
        setTablePage(1);
    }, [selectedHandler]);

    useEffect(() => {
        resetFilters();
    }, [selectedHandler, resetFilters]);

    const fetchTableRows = useCallback(async () => {
        if (!selectedHandler) return;
        setIsTableLoading(true);
        try {
            // await new Promise((resolve) => setTimeout(resolve, 3000));
            const { rows, total } = await selectedHandler.fetchList({
                ...selectedHandler.defaultFilters,
                ...tableFilters,
                page: tablePage,
                limit: PAGE_SIZE,
            });
            setTableRows(rows || []);
            setTableTotal(total || 0);
        } catch (error) {
            showErrorRef.current(getReadableErrorMessage(error));
        } finally {
            setIsTableLoading(false);
        }
    }, [selectedHandler, tableFilters, tablePage]);

    useEffect(() => {
        fetchTableRows();
    }, [fetchTableRows]);

    const handleFilterChange = (key, value) => {
        setTableFilters((prev) => ({ ...prev, [key]: value }));
        setTablePage(1);
    };

    const handleOpenForm = () => {
        setFormMode("create");
        setEditingLicense(null);
        setIsProductSelectorOpen(true);
    };

    const handleGenerateProduct = async (productKey) => {
        const handler = productHandlers[productKey];

        if (!handler) {
            showError("Product not supported yet.");
            return;
        }

        setIsPreparingForm(true);

        try {
            const preparation = await handler.prepareForm();

            if (!preparation?.ok) return;
            setFormProductKey(productKey);

            setProductFormData((prev) => ({
                ...prev,
                [productKey]: preparation.data || {},
            }));

            setIsProductSelectorOpen(false);
            setIsFormOpen(true);
        } finally {
            setIsPreparingForm(false);
        }
    };

    /**
     * Opens the form on an existing license.
     *
     * The same prepareForm() the create flow uses: an edit still needs the
     * client list and the current model/feature catalog, and re-fetching keeps
     * a long-open tab from editing against a catalog that has since changed.
     */
    const handleEditLicense = async (row) => {
        if (!selectedProductKey) return;

        const handler = productHandlers[selectedProductKey];

        if (!handler?.submitEdit) {
            showError(`${selectedProduct?.label || "This product"} does not support editing yet.`);
            return;
        }

        setIsPreparingForm(true);

        try {
            const preparation = await handler.prepareForm();
            if (!preparation?.ok) return;

            setProductFormData((prev) => ({
                ...prev,
                [selectedProductKey]: preparation.data || {},
            }));

            setFormProductKey(selectedProductKey);
            setEditingLicense(row);
            setFormMode("edit");
            setIsFormOpen(true);
        } finally {
            setIsPreparingForm(false);
        }
    };

    const handleSubmitSelectedProduct = async (payload) => {
        if (!formProductKey) {
            showError("Please select a product first.");
            return;
        }

        const handler = productHandlers[formProductKey];
        const isEdit = formMode === "edit" && editingLicense;

        const submit = isEdit
            ? () => handler?.submitEdit?.(editingLicense.licenseId, payload)
            : () => handler?.submitForm?.(payload);

        if (isEdit ? !handler?.submitEdit : !handler?.submitForm) {
            showError(`${formProduct?.label || "Selected product"} ${isEdit ? "edit" : "submit"} flow is not implemented.`);
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await submit();
            if (!result?.ok) return;
            setIsFormOpen(false);
            setEditingLicense(null);
            setFormMode("create");
            fetchTableRows();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDownload = async (licenseId) => {
        if (!selectedProductKey) return;

        try {
            const config = selectedProductKey === "trinetra"
                ? API_CONFIG.TRINETRA_LICENSE.DOWNLOAD
                : API_CONFIG.LICENSE.DOWNLOAD;

            const endpoint = config.ENDPOINT.replace(":licenseId", licenseId);
            const res = await callApi(config.METHOD, endpoint, null, { "Content-Type": "application/json" }, null, "blob");

            if (!res.data || res.data.size === 0) throw new Error("Empty response");

            const blob = new Blob([res.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${licenseId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showSuccess("License downloaded successfully");
        } catch (err) {
            showError(getReadableErrorMessage(err));
        }
    };

    const handleDeleteLicense = async () => {
        if (!deletingLicense || !selectedProductKey) return;

        setIsDeleting(true);
        try {
            const config = selectedProductKey === "trinetra"
                ? API_CONFIG.TRINETRA_LICENSE.DELETE
                : API_CONFIG.LICENSE.DELETE;

            await callApi(
                config.METHOD,
                config.ENDPOINT.replace(":licenseId", deletingLicense.licenseId)
            );

            showSuccess("License deleted successfully");
            setDeletingLicense(null);

            // Removing the last row on a page would otherwise leave the table
            // empty on a page that no longer exists; stepping back refetches.
            if (tableRows.length === 1 && tablePage > 1) {
                setTablePage(tablePage - 1);
            } else {
                fetchTableRows();
            }
        } catch (err) {
            showError(getReadableErrorMessage(err));
        } finally {
            setIsDeleting(false);
        }
    };

    if (isPageLoading) {
        return <PageSkeleton />;
    }

    // Built once per render rather than per row — the previous version called
    // tableColumns() twice inside the row loop, rebuilding every column
    // definition for every row on screen.
    const columns = selectedHandler
        ? selectedHandler.tableColumns({
            onDownload: handleDownload,
            onDelete: setDeletingLicense,
            // Left undefined for products with no edit flow, so their column
            // renderer can leave the button out entirely.
            onEdit: selectedHandler.submitEdit ? handleEditLicense : undefined,
        })
        : [];

    return (
        <div className="p-4 sm:p-6 min-h-screen w-full" style={{ background: "var(--main-bg)", color: "var(--main-text)" }}>
            <PageHeader
                icon={<FileLock size={28} strokeWidth={2.2} />}
                title="License Generator"
                subtitle="Select a product, apply filters, and view the product-specific license information"
                meta={
                    selectedProduct
                        ? `Product: ${selectedProduct.label}`
                        : "No product selected"
                }
                actions={
                    <button
                        onClick={handleOpenForm}
                        disabled={isPreparingForm}
                        className="h-10 rounded-lg px-4 font-medium text-white flex items-center justify-center gap-2 transition disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{ background: "var(--brand)" }}
                    >
                        <Plus size={16} />
                        <span>{isPreparingForm ? "Loading..." : "Generate License"}</span>
                    </button>
                }
            />

            <LicenseFilterSection
                products={products}
                selectedProductKey={selectedProductKey}
                setSelectedProductKey={setSelectedProductKey}
                selectedHandler={
                    selectedHandler
                        ? {
                            ...selectedHandler,
                            filterDefinitions:
                                selectedHandler.getFilterDefinitions?.(filterMetadata) || [],
                        }
                        : null
                }
                tableFilters={tableFilters}
                handleFilterChange={handleFilterChange}
                resetFilters={resetFilters}
                fetchTableRows={fetchTableRows}
                defaultProductKey={defaultProductKey}
                setDefaultProductKey={setDefaultProductKey}
            />

            {/* Without a product there are no columns to render a table from,
                so the panel stands in for it entirely. */}
            {!selectedHandler ? (
                <div
                    className="rounded-lg border"
                    style={{ background: "var(--card-bg)", borderColor: "var(--card-border)" }}
                >
                    <EmptyState
                        icon={<PackageSearch size={26} />}
                        title="No product selected"
                        description="Pick a product in the Product dropdown above to view its license records."
                    />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    rows={tableRows}
                    loading={isTableLoading}
                    getRowKey={(row, index) => row._id || row.licenseId || index}
                    emptyState={
                        <EmptyState
                            icon={<FileLock size={26} />}
                            title={`No ${selectedProduct?.label || "product"} licenses found`}
                            description="No records match the current filters. Reset them, or generate a license for this product."
                            action={
                                <button
                                    onClick={handleOpenForm}
                                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium"
                                    style={{ background: "var(--brand)", color: "#fff" }}
                                >
                                    <Plus size={18} /> Generate License
                                </button>
                            }
                        />
                    }
                />
            )}

            <Pagination
                page={tablePage}
                totalPages={Math.ceil(tableTotal / PAGE_SIZE)}
                totalRecords={selectedHandler ? tableTotal : null}
                onPageChange={setTablePage}
            />

            {isProductSelectorOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div
                        className="w-full max-w-2xl rounded-2xl overflow-hidden"
                        style={{
                            background: "var(--card-bg)",
                            border: "1px solid var(--card-border)",
                        }}
                    >
                        {/* Header */}
                        <div
                            className="flex items-center justify-between px-6 py-4 border-b"
                            style={{ borderColor: "var(--card-border)" }}
                        >
                            <div>
                                <h3
                                    className="text-xl font-semibold"
                                    style={{ color: "var(--main-text)" }}
                                >
                                    Generate License
                                </h3>
                                <p
                                    className="text-sm mt-1"
                                    style={{ color: "var(--main-text-muted)" }}
                                >
                                    Select a product to generate a license
                                </p>
                            </div>

                            <button
                                onClick={() => setIsProductSelectorOpen(false)}
                                className="p-2 rounded-lg hover:bg-black/5"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Product List */}
                        <div className="p-6">
                            <div className="space-y-4">
                                {products.map((product) => (
                                    <div
                                        key={product.key}
                                        className="flex items-center justify-between rounded-xl border p-4 transition-all"
                                        style={{
                                            borderColor: "var(--card-border)",
                                            background: "var(--main-bg)",
                                        }}
                                    >
                                        <div>
                                            <h4
                                                className="font-semibold text-base"
                                                style={{ color: "var(--main-text)" }}
                                            >
                                                {product.label}
                                            </h4>
                                        </div>

                                        <button
                                            onClick={() =>
                                                handleGenerateProduct(product.key)
                                            }
                                            disabled={isPreparingForm}
                                            className="h-10 px-5 rounded-lg text-white font-medium flex items-center gap-2"
                                            style={{
                                                background: "var(--brand)",
                                            }}
                                        >
                                            <Plus size={16} />
                                            Generate
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isFormOpen && formHandler && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
                    <div className="rounded-lg w-full max-w-2xl p-6" style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)" }}>
                        <div className="flex justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold" style={{ color: "var(--main-text)" }}>
                                    {formMode === "edit"
                                        ? `Edit ${formProduct?.label || "License"}`
                                        : formProduct?.label}
                                </h3>
                                {formMode === "edit" && (
                                    <p className="text-sm mt-1" style={{ color: "var(--main-text-muted)" }}>
                                        Change the limits, features and parameters, then re-sign
                                        for the same hardware
                                    </p>
                                )}
                            </div>

                            <button
                                onClick={() => setIsFormOpen(false)}
                                disabled={isSubmitting}
                                className="p-2 rounded-lg hover:bg-black/5 self-start"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        {formHandler.renderForm({
                            onSubmit: handleSubmitSelectedProduct,
                            isLoading: isSubmitting,
                            formData: productFormData[formProductKey] || {},
                            mode: formMode,
                            license: editingLicense,
                        })}
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={Boolean(deletingLicense)}
                title="Delete license"
                message={
                    <>
                        Permanently delete license{" "}
                        <strong style={{ color: "var(--main-text)" }}>
                            {deletingLicense?.licenseId}
                        </strong>
                        ? The signed .lic file is removed from the server, but any copy
                        already delivered to the customer keeps working until it expires.
                    </>
                }
                details={
                    deletingLicense && (
                        <div className="space-y-1">
                            <div>Product: {selectedProduct?.label}</div>
                            <div>Type: {deletingLicense.licenseType}</div>
                            <div>
                                Expires:{" "}
                                {deletingLicense.expiresAt
                                    ? new Date(deletingLicense.expiresAt).toLocaleDateString("en-GB")
                                    : "Never (Perpetual)"}
                            </div>
                            <div className="pt-1">
                                This frees the machine's slot, so a replacement license can be
                                issued for the same hardware.
                            </div>
                        </div>
                    )
                }
                confirmLabel="Delete license"
                isLoading={isDeleting}
                onConfirm={handleDeleteLicense}
                onCancel={() => setDeletingLicense(null)}
            />
        </div>
    );
};

export default FinalLicenseGeneration;
